import { useEffect } from 'react';

/**
 * Plausible (cookieless) analytics loader.
 *
 * Behaviour:
 *   • If REACT_APP_PLAUSIBLE_DOMAIN is set AND the current hostname matches
 *     that domain (or a subdomain of it), injects the Plausible script and
 *     exposes window.plausible(eventName, options) for custom events.
 *   • Otherwise (env empty, or running on the preview / localhost),
 *     stubs window.plausible as a no-op so existing event calls remain
 *     side-effect-free and never throw.
 *
 * Why the hostname guard: the production env baked into the build sets the
 * domain to `mobilecorex.ng`, but the same bundle is also served from the
 * preview domain — we don't want preview pageviews polluting Plausible.
 *
 * GDPR/NDPA: Plausible uses no cookies and stores no PII. No banner required.
 */
function shouldEnable(domain) {
  if (!domain) return false;
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === domain || host.endsWith(`.${domain}`);
}

function installStub() {
  if (typeof window !== 'undefined' && typeof window.plausible !== 'function') {
    window.plausible = () => {};
  }
}

export default function PlausibleAnalytics() {
  useEffect(() => {
    const domain = process.env.REACT_APP_PLAUSIBLE_DOMAIN;

    if (!shouldEnable(domain)) {
      installStub();
      return undefined;
    }

    if (document.querySelector('script[data-mcx-plausible]')) return undefined;

    // Pre-load queueing shim so window.plausible(...) calls fired before the
    // remote script finishes loading are not lost.
    if (typeof window !== 'undefined' && typeof window.plausible !== 'function') {
      window.plausible = function queue(...args) {
        (window.plausible.q = window.plausible.q || []).push(args);
      };
    }

    const script = document.createElement('script');
    script.defer = true;
    script.setAttribute('data-domain', domain);
    script.setAttribute('data-mcx-plausible', 'true');
    script.src = 'https://plausible.io/js/script.tagged-events.js';
    document.head.appendChild(script);

    return undefined;
  }, []);

  return null;
}
